/**
 * Profile RPC group.
 *
 * Read/mutate `InstallationProfileLayer` rows, render a preview of the
 * composed profile, and stream `InstallationConfigLog` events. Read +
 * mutate endpoints are gated by `AuthMiddleware`; `appendConfigLog` is
 * exclusively gated by `MgmtAgentMiddleware` (gateway-token auth from
 * the VPS-side management agent).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { InstallationConfigEventKind, InstallationConfigLog } from "@fm/db/models/InstallationConfigLog";
import { InstallationProfileLayer, InstallationProfileLayerKind } from "@fm/db/models/InstallationProfileLayer";
import { InstallationProfilePublish } from "@fm/db/models/InstallationProfilePublish";
import { Schema } from "effect";

import { OpenclawConfigPatch, ProfileLayerData } from "@fm/cluster/profile/schema";
import { AuthMiddleware } from "../rpc/auth.tags.ts";
import { DatabaseError, RecordNotFoundError, Unauthorized } from "../rpc/errors.ts";
import { MgmtAgentMiddleware } from "./middleware/mgmt-agent.tags.ts";

/**
 * Wire-typed render error returned by `renderProfile` when layer data
 * fails to decode against `ProfileLayerData`.
 *
 * @since 1.0.0
 */
export class RenderError extends Schema.TaggedError<RenderError>()(
  "RenderError",
  {
    kind: Schema.Literal(
      "invalid_layer_data",
      "missing_required_field",
      "duplicate_account_key",
    ),
    message: Schema.String,
  },
) {}

/**
 * Wire-typed error returned by `publish` when the rendered profile
 * references a private skill archive `(slug, version)` that has no
 * `skill_archives` row. Fail-closed: a box must never be told to install
 * an archive that does not exist, so the publish is rejected before any
 * row is persisted or command enqueued.
 *
 * @since 1.0.0
 */
export class SkillArchiveRefNotFoundError extends Schema.TaggedError<SkillArchiveRefNotFoundError>()(
  "SkillArchiveRefNotFoundError",
  {
    slug: Schema.String,
    version: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Wire-typed validation error returned by `upsertLayer` when the
 * supplied `data` payload does not conform to `ProfileLayerData`.
 *
 * @since 1.0.0
 */
export class InvalidLayerData extends Schema.TaggedError<InvalidLayerData>()(
  "InvalidLayerData",
  {
    message: Schema.String,
  },
) {}

/**
 * Wire-typed error for `rollback` when the requested version does not
 * exist for the installation.
 *
 * @since 1.0.0
 */
export class PublishVersionNotFound extends Schema.TaggedError<PublishVersionNotFound>()(
  "PublishVersionNotFound",
  {
    installationId: Schema.String,
    version: Schema.Number,
  },
) {}

/**
 * Wire-typed error for `publish` when the command stream enqueue fails.
 *
 * @since 1.0.0
 */
export class CommandDispatchError extends Schema.TaggedError<CommandDispatchError>()(
  "CommandDispatchError",
  {
    message: Schema.String,
  },
) {}

/**
 * Trigger payload for `auth_failed` config-log events. Posted by
 * OpenClaw via the existing `appendConfigLog` RPC when its own runtime
 * token-refresh attempt fails. The re-auth banner on the install detail
 * page reads these to surface affected providers (deduped by
 * `provider`+`connectionId`) with the operator-facing `reason`.
 *
 * @since 1.0.0
 */
export const AuthFailedTrigger = Schema.Struct({
  provider: Schema.String,
  connectionId: Schema.String,
  reason: Schema.String,
});
export type AuthFailedTrigger = Schema.Schema.Type<typeof AuthFailedTrigger>;

/**
 * Wire-typed validation error returned by `appendConfigLog` when the
 * supplied `trigger` payload does not match the schema required for the
 * given `eventKind` (currently only enforced for `auth_failed`).
 *
 * @since 1.0.0
 */
export class InvalidTriggerPayload extends Schema.TaggedError<InvalidTriggerPayload>()(
  "InvalidTriggerPayload",
  {
    eventKind: InstallationConfigEventKind,
    message: Schema.String,
  },
) {}

/**
 * Response shape for `publish` and `rollback`. Carries the new current
 * snapshot row and the dispatched command id for client-side correlation.
 *
 * @since 1.0.0
 */
export const ProfilePublishResponse = Schema.Struct({
  publish: InstallationProfilePublish.json,
  commandId: Schema.String,
});
export type ProfilePublishResponse = Schema.Schema.Type<typeof ProfilePublishResponse>;

/**
 * Rendered-profile output shape used by `renderProfile`.
 *
 * @since 1.0.0
 */
export const RenderedProfileResponse = Schema.Struct({
  openclawConfigPatch: OpenclawConfigPatch,
  workspaceFiles: Schema.Record({ key: Schema.String, value: Schema.String }),
  credentials: Schema.Record({ key: Schema.String, value: Schema.String }),
  plugins: Schema.Array(Schema.Struct({ spec: Schema.String })),
});
export type RenderedProfileResponse = Schema.Schema.Type<typeof RenderedProfileResponse>;

/**
 * Profile RPC group. All read/mutate endpoints require `AuthMiddleware`
 * (Clerk session). `appendConfigLog` requires `MgmtAgentMiddleware`
 * (gateway-token auth) and rejects any user-auth attempt.
 *
 * @since 1.0.0
 */
export class ProfileRpcs extends RpcGroup.make(
  Rpc.make("listLayers", {
    payload: Schema.Struct({ installationId: Schema.String }),
    success: Schema.Array(InstallationProfileLayer.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("getLayer", {
    payload: Schema.Struct({
      installationId: Schema.String,
      kind: InstallationProfileLayerKind,
    }),
    success: Schema.NullOr(InstallationProfileLayer.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("upsertLayer", {
    payload: Schema.Struct({
      installationId: Schema.String,
      kind: InstallationProfileLayerKind,
      data: ProfileLayerData,
    }),
    success: InstallationProfileLayer.json,
    error: Schema.Union(Unauthorized, DatabaseError, InvalidLayerData),
  }).middleware(AuthMiddleware),
  Rpc.make("renderProfile", {
    payload: Schema.Struct({ installationId: Schema.String }),
    success: RenderedProfileResponse,
    error: Schema.Union(Unauthorized, DatabaseError, RenderError),
  }).middleware(AuthMiddleware),
  Rpc.make("listConfigLog", {
    payload: Schema.Struct({
      installationId: Schema.String,
      limit: Schema.optional(Schema.Number),
      before: Schema.optional(Schema.DateTimeUtc),
    }),
    success: Schema.Array(InstallationConfigLog.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("listHourlyTags", {
    payload: Schema.Struct({ installationId: Schema.String }),
    success: Schema.Array(InstallationConfigLog.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("listPublishes", {
    payload: Schema.Struct({ installationId: Schema.String }),
    success: Schema.Array(InstallationProfilePublish.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("publish", {
    payload: Schema.Struct({ installationId: Schema.String }),
    success: ProfilePublishResponse,
    error: Schema.Union(
      Unauthorized,
      DatabaseError,
      RenderError,
      CommandDispatchError,
      SkillArchiveRefNotFoundError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("rollback", {
    payload: Schema.Struct({
      installationId: Schema.String,
      version: Schema.Number,
    }),
    success: ProfilePublishResponse,
    error: Schema.Union(
      Unauthorized,
      DatabaseError,
      PublishVersionNotFound,
      CommandDispatchError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("appendConfigLog", {
    payload: Schema.Struct({
      installationId: Schema.String,
      sha: Schema.NullOr(Schema.String),
      eventKind: InstallationConfigEventKind,
      author: Schema.String,
      layer: Schema.NullOr(Schema.String),
      message: Schema.NullOr(Schema.String),
      filesChanged: Schema.NullOr(Schema.Unknown),
      trigger: Schema.NullOr(Schema.Unknown),
    }),
    success: InstallationConfigLog.json,
    error: Schema.Union(Unauthorized, DatabaseError, RecordNotFoundError, InvalidTriggerPayload),
  }).middleware(MgmtAgentMiddleware),
) {}
